import { curry, defaultsDeep, filter, get, map, mapKeys, pick, sample, sampleSize, toArray } from 'lodash-es';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { buildArrayOfRefs } from 'client/data/luckdragon/json-ref';
import { EdmundsAPI } from 'client/data/api/api-client';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { withMetrics } from 'client/data/api/api-metrics';
import { DEFAULT_SEO_CONTEXT, SITE_URL } from 'site-modules/shared/pages/seo';
import { VehicleModel } from './vehicle';
import { PageModel } from './page';

const PUB_STATES = ['NEW', 'NEW_USED'];
const filterPubStsFn = item => PUB_STATES.some(st => item.pubStates[st]);

const pickNameAndSlug = make =>
  mapKeys(pick(make, ['name', 'niceName']), (_value, key) => (key === 'niceName' ? 'slug' : key));
const selectRandomModel = make => pickNameAndSlug(sample(get(make, 'models')));
const buildMMYObject = curry((year, make) => ({
  make: pickNameAndSlug(make),
  model: selectRandomModel(make),
  year,
}));

function getRandomizedMakes(count, context) {
  const takeNumberOfRandomMakes = makes => sampleSize(filter(toArray(makes), filterPubStsFn), count);
  return context.resolveValue('makes', VehicleModel).then(takeNumberOfRandomMakes);
}

function getCount(match) {
  return parseInt(match.count, 10);
}

function getMakeModelsByYear(year, count, context) {
  const getMakeModels = response => sampleSize(get(response, 'makes'), count);
  const url = `/vehicle/v2/makes?year=${year}`;
  return withMetrics(EdmundsAPI, context)
    .fetch(url)
    .then(response => response.json())
    .then(getMakeModels);
}

export function getBestRankings() {
  return 'bestRankings';
}

export function getBestRankingsForAboveTheFold() {
  return 'aboveTheFold.bestRankings';
}

export function getBestRankingsForBelowTheFold() {
  return 'belowTheFold.bestRankings';
}

export function hasYearInUrl(year, url) {
  const pathSegments = (url || '').split('/');
  return pathSegments.includes(String(year));
}

async function resolveContent(match, context) {
  const { year } = match;
  const pathname = await context.resolveValue('location.pathname', PageModel);
  const hasYearInPath = hasYearInUrl(year, pathname);
  const yearPath = year && !hasYearInPath ? `${year}/` : '';
  const url = `/seo/v1/content/?url=${SITE_URL}${pathname}${yearPath}`;
  return withMetrics(EdmundsAPI, context)
    .fetchJson(url)
    .then(data => {
      const { firstSentence } = data || {};
      if (!firstSentence) {
        return data || {};
      }
      try {
        return {
          ...data,
          firstSentence: JSON.parse(firstSentence),
        };
      } catch {
        return data || {};
      }
    });
}

export const SEOModel = createModelSegment('seo', [
  {
    path: 'headContent',
    update(data) {
      return Promise.resolve(defaultsDeep(data, DEFAULT_SEO_CONTEXT));
    },
  },
  {
    path: 'randomizedMakes.Count.{count}',
    resolve(match, context) {
      return getRandomizedMakes(getCount(match), context);
    },
  },
  {
    path: 'randomizedMakeModel.{year}.Count.{count}',
    resolve(match, context) {
      return getMakeModelsByYear(match.year, getCount(match), context).then(makes =>
        map(makes, buildMMYObject(match.year))
      );
    },
  },
  {
    path: 'pageProfile',
    async resolve(match, context) {
      const pathname = await context.resolveValue('location.pathname', PageModel);
      const url = `/seo/v1/pageprofile/?url=${SITE_URL}${pathname}`;

      return withMetrics(EdmundsAPI, context).fetchJson(url);
    },
  },
  {
    path: 'seoExperiments',
    async resolve(match, context) {
      const pathname = await context.resolveValue('location.pathname', PageModel);
      const url = `/seo/v1/experiments/?url=${SITE_URL}${pathname}`;
      const extendedApiCache = await context.resolveValue('extendedApiCache', FeatureFlagModel);

      return withMetrics(EdmundsAPI, context)
        .fetchJson(
          url,
          { timeout: 1000, useVenomCache: extendedApiCache } // Added for CORE caching
        )
        .then(data => get(data, 'experiments'))
        .catch(() => []);
    },
  },
  {
    path: 'bestRankings',
    async resolve(match, context) {
      const pathname = await context.resolveValue('location.pathname', PageModel);
      const url = `/seo/v1/rankings/?url=${pathname}`;
      return withMetrics(EdmundsAPI, context)
        .fetchJson(url)
        .then(({ rankings }) => rankings.sort((a, b) => Number(a.id) - Number(b.id)))
        .catch(() => []);
    },
  },
  {
    path: 'aboveTheFold.bestRankings',
    resolve(match, context) {
      return context.resolveValue('bestRankings').then(() => buildArrayOfRefs(0, 1, getBestRankings()));
    },
  },
  {
    path: 'belowTheFold.bestRankings',
    resolve(match, context) {
      return context
        .resolveValue('bestRankings')
        .then(rankings => buildArrayOfRefs(1, rankings?.length, getBestRankings()));
    },
  },
  {
    path: 'content.{year}',
    resolve: resolveContent,
  },
  {
    path: 'content',
    resolve: resolveContent,
  },
]);
